// Single source of truth for verified case studies.
// Data source: India_AI_CFO_Companies_FULLY_AUDITED_2026-07-13.xlsx
// Sheet: "Customer Case Studies" (23 verified rows).
//
// Used by both the marquee on the landing page and the case-study
// detail route (/case-study/:id).

const CASES = [
  // ─── Bluecopa (7) ─────────────────────────────────────────────────────
  {
    id: "bluecopa-yatra",
    vendor: "Bluecopa",
    vendorUrl: "https://www.bluecopa.com",
    sourceUrl: "https://www.bluecopa.com/customers/yatra",
    customer: "Yatra",
    customerBio:
      "Listed Indian online travel company handling corporate and leisure bookings across airlines, hotels and packages.",
    industry: "Travel · OTA",
    industryKey: "hospitality",
    size: "Publicly listed",
    metric: "+75%",
    metricLabel: "PLB revenue recovery",
    summary:
      "Airline PLB leakage eliminated. Reports from 10 days to 30 minutes.",
    problem:
      "Manual invoice and corporate collection reconciliation across airline files, sales and accrual data. Spreadsheet errors were causing performance-linked bonus (PLB) leakage and slow reporting.",
    solution:
      "Bluecopa automated ingestion of 16 large airline files, sales and accrual data into an MIS dashboard with continuous reconciliation and audit trails.",
    improvements:
      "Ingesting the initial 16 large files still required staged validation for legacy PLB categories.",
    timeline: "Not disclosed publicly",
    roi: [
      "75% improvement in PLB revenue recovery",
      "3x faster revenue recognition",
      "7x faster AR reconciliation",
      "80% less manual effort · 90% faster close",
      "Reports from 10 days to 30 minutes",
      "16 large files ingested — 7 days to 2 hours",
    ],
    latest: true,
    viz: "bars",
    color: "emerald",
  },
  {
    id: "bluecopa-porter",
    vendor: "Bluecopa",
    vendorUrl: "https://www.bluecopa.com",
    sourceUrl:
      "https://www.bluecopa.com/customers/how-porter-automated-70-of-reconciliation-tasks",
    customer: "Porter",
    customerBio:
      "Indian intra-city logistics platform operating trucks and two-wheelers across 22 cities and seven verticals.",
    industry: "Logistics",
    industryKey: "other",
    size: "22 cities · 7 verticals",
    metric: "DSO 3-5d",
    metricLabel: "from >15 days",
    summary: "70% less reconciliation effort. 6x faster close. 3x faster cash conversion.",
    problem:
      "Receivables visibility lagged by more than 10 days. Reconciliation and month-end close were bogged down across booking, payout, billing and shipment systems.",
    solution:
      "Bluecopa unified data across booking, payout, billing and shipment systems into AR dashboards with automated matching and continuous close.",
    improvements:
      "Mapping payout formats across 22 cities and seven verticals was iterative in the first cycle.",
    timeline: "Not disclosed publicly",
    roi: [
      "DSO cut from >15 days to 3-5 days",
      "70% less reconciliation effort",
      "6x faster close",
      "3x faster cash conversion",
      "5x faster incentives payout",
    ],
    viz: "line",
    color: "amber",
  },
  {
    id: "bluecopa-purplle",
    vendor: "Bluecopa",
    vendorUrl: "https://www.bluecopa.com",
    sourceUrl:
      "https://www.bluecopa.com/customers/how-purplle-accelerated-month-end-close-by-50",
    customer: "Purplle",
    customerBio:
      "Indian D2C beauty and personal-care e-commerce platform.",
    industry: "Beauty · E-commerce",
    industryKey: "retail",
    size: "D2C e-commerce",
    metric: "7x",
    metricLabel: "faster reconciliation",
    summary: "High-volume payment reconciliation and month-end close, automated.",
    problem:
      "High-volume payment reconciliation and month-end close were fully manual, slowing finance visibility.",
    solution:
      "Bluecopa's automated reconciliation engine matched payment gateway, marketplace and bank data at source, halving month-end close time.",
    improvements: "Other implementation details are not publicly disclosed.",
    timeline: "Not disclosed publicly",
    roi: [
      "7x faster payment reconciliation",
      "Month-end close accelerated by 50%",
      "Manual matching eliminated",
    ],
    viz: "heat",
    color: "sky",
  },
  {
    id: "bluecopa-diversey",
    vendor: "Bluecopa",
    vendorUrl: "https://www.bluecopa.com",
    sourceUrl: "https://www.bluecopa.com/customers/diversey",
    customer: "Diversey",
    customerBio:
      "Global cleaning, hygiene and infection-prevention solutions provider.",
    industry: "CPG · Hygiene",
    industryKey: "cpg",
    size: "Global",
    metric: "-99%",
    metricLabel: "distributor calls",
    summary: "Continuously updating distributor scorecards across ERPs, SAP, Excel and email.",
    problem:
      "Fragmented distributor-performance and claims data was causing repeated calls from distributors chasing status.",
    solution:
      "Bluecopa ingested distributor ERPs, SAP, Excel, email, sales, inventory and claims into continuously updating scorecards.",
    improvements: "Data-quality gaps in legacy distributor ERPs surfaced during onboarding.",
    timeline: "Not disclosed publicly",
    roi: [
      "99% reduction in distributor calls",
      "Continuously refreshed distributor scorecards",
      "Consolidated view across ERPs, SAP, Excel and email",
    ],
    viz: "bars",
    color: "emerald",
  },
  {
    id: "bluecopa-premier-ecommerce",
    vendor: "Bluecopa",
    vendorUrl: "https://www.bluecopa.com",
    sourceUrl:
      "https://www.bluecopa.com/customers/how-indias-premier-e-commerce-platform",
    customer: "India's premier e-commerce platform",
    customerBio:
      "Large Indian marketplace (identity withheld in the official case study).",
    industry: "E-commerce",
    industryKey: "retail",
    size: "Enterprise marketplace",
    metric: "10x",
    metricLabel: "faster reporting",
    summary: "Seller-payout reconciliation from hours to minutes; 10+ audit-ready reports.",
    problem:
      "Large files and fragmented workflows slowed seller payout reconciliation and reporting.",
    solution:
      "Cloud ingestion and processing plus an Excel-like UI let finance teams work at data-warehouse scale without SQL.",
    improvements: "Identity is withheld in the official case study.",
    timeline: "Not disclosed publicly",
    roi: [
      "10x faster reporting",
      "Ingestion from 2 hours to 20 minutes",
      "Processing from 8 hours to 45 minutes",
      "10+ audit-ready reports",
    ],
    viz: "line",
    color: "amber",
  },
  {
    id: "bluecopa-ekacare",
    vendor: "Bluecopa",
    vendorUrl: "https://www.bluecopa.com",
    sourceUrl: "https://www.bluecopa.com/customers/eka-care",
    customer: "EkaCare",
    customerBio:
      "Indian digital health platform for patients, doctors and clinics.",
    industry: "Healthcare · Digital Health",
    industryKey: "healthcare",
    size: "Digital health platform",
    metric: "+85%",
    metricLabel: "operational efficiency",
    summary: "Finance operations automated across a digital health platform.",
    problem: "Finance operations required automation across a fast-growing platform.",
    solution:
      "Bluecopa automated ingestion, reconciliation and reporting across EkaCare's finance workflows.",
    improvements: "Detailed integration stack and timeline are not publicly disclosed.",
    timeline: "Not disclosed publicly",
    roi: [
      "85% operational-efficiency improvement",
      "Manual finance workflows automated",
      "Faster close and reporting",
    ],
    viz: "heat",
    color: "sky",
  },
  {
    id: "bluecopa-global-hr-saas",
    vendor: "Bluecopa",
    vendorUrl: "https://www.bluecopa.com",
    sourceUrl: "https://www.bluecopa.com/customers/global-hr-saas-platform",
    customer: "Global HR SaaS platform",
    customerBio:
      "Singapore-founded HCM provider serving 900+ brands across 130+ countries (identity withheld).",
    industry: "SaaS · HCM",
    industryKey: "saas",
    size: "900+ brands · 130+ countries",
    metric: "80%",
    metricLabel: "of AR automated",
    summary: "Highly manual global AR operations automated end-to-end.",
    problem: "Global AR operations were highly manual across many geographies and entities.",
    solution:
      "Bluecopa automated 80% of AR operations across the customer's global entities and payment channels.",
    improvements: "Identity is withheld in the official case study.",
    timeline: "Not disclosed publicly",
    roi: [
      "80% of AR operations automated",
      "Standardised across 130+ countries",
      "Single view for global finance leadership",
    ],
    viz: "bars",
    color: "emerald",
  },

  // ─── Zenskar (7) ──────────────────────────────────────────────────────
  {
    id: "zenskar-100ms",
    vendor: "Zenskar",
    vendorUrl: "https://www.zenskar.com",
    sourceUrl: "https://www.zenskar.com/case-study/100ms",
    customer: "100ms",
    customerBio:
      "Video infrastructure platform for developers — programmable APIs for real-time video and audio.",
    industry: "Video Infrastructure · SaaS",
    industryKey: "saas",
    size: "Early-stage startup",
    metric: "+4%",
    metricLabel: "revenue reclaimed",
    summary: "Configurable usage billing live in 3 days. Stripe Billing replaced.",
    problem: "Legacy billing could not handle 100ms's complex usage-based pricing model.",
    solution:
      "Zenskar replaced Stripe Billing with a configurable contract builder, bulk contract upload and usage metering — live in 3 days.",
    improvements: "Bespoke metered events required data-engineering collaboration in week one.",
    timeline: "3 days",
    roi: [
      "4% revenue increase from eliminating underbilling",
      "~250 hours / month saved on the finance team",
      "Stripe Billing replaced end-to-end",
    ],
    viz: "line",
    color: "sky",
  },
  {
    id: "zenskar-yembo",
    vendor: "Zenskar",
    vendorUrl: "https://www.zenskar.com",
    sourceUrl: "https://www.zenskar.com/case-study/yembo",
    customer: "Yembo",
    customerBio:
      "San Diego AI computer-vision platform turning smartphone video into virtual home surveys.",
    industry: "AI · Home Services",
    industryKey: "saas",
    size: "Startup, founded 2016",
    metric: "-90%",
    metricLabel: "finance bandwidth",
    summary: "Revenue leakage eliminated. Half of revenue collected a month earlier.",
    problem: "Revenue leakage and manual billing and collections across HubSpot and Stripe.",
    solution:
      "Zenskar integrated with HubSpot and Stripe, consolidated prepaid + postpaid charges and automated dunning.",
    improvements: "Switching away from a legacy tool required manual close-out of running subscriptions.",
    timeline: "Migrated within a single billing cycle",
    roi: [
      "~90% of finance bandwidth saved",
      "50% of revenue collected a month earlier",
      "Revenue leakage eliminated",
    ],
    viz: "heat",
    color: "amber",
  },
  {
    id: "zenskar-squadstack",
    vendor: "Zenskar",
    vendorUrl: "https://www.zenskar.com",
    sourceUrl: "https://www.zenskar.com/case-study/squadstack",
    customer: "SquadStack",
    customerBio: "Outsourced sales-as-a-service platform powered by conversational AI.",
    industry: "Sales · Conversational AI",
    industryKey: "saas",
    size: "170,000+ leads / day",
    metric: "170k+",
    metricLabel: "leads billed daily",
    summary: "Complex per-lead pricing and Zoho Books billing, fully automated.",
    problem:
      "Complex pricing required manual calculations and cross-team dependencies between finance, ops and engineering.",
    solution:
      "Zenskar automated billing and usage reports for 170,000+ leads per day, wired into Zoho Books.",
    improvements: "Historical spreadsheet reconciliation was a one-time manual lift.",
    timeline: "Not disclosed publicly",
    roi: [
      "Automated billing for 170,000+ leads / day",
      "Cross-team dependencies eliminated",
      "Zoho Books integrated end-to-end",
    ],
    viz: "bars",
    color: "emerald",
  },
  {
    id: "zenskar-pontera",
    vendor: "Zenskar",
    vendorUrl: "https://www.zenskar.com",
    sourceUrl: "https://www.zenskar.com/case-study/pontera",
    customer: "Pontera",
    customerBio:
      "NYC-based fintech helping financial advisors manage held-away retirement accounts.",
    industry: "Fintech · Wealth Management",
    industryKey: "fintech",
    size: "1,500+ clients billed",
    metric: "$12k / mo",
    metricLabel: "AR ops saved",
    summary: "500+ customers billed in 2 days. $5M+ collected in one click.",
    problem: "Billing 500+ customers manually took weeks, with missed payments and reconciliation drift.",
    solution:
      "Zenskar automated invoicing, dunning and cash application with a NetSuite integration and journal auto-posting.",
    improvements: "Migration of 12+ months of historical AR data required staged reconciliation.",
    timeline: "Full replacement in ~8 weeks",
    roi: [
      "500+ customers billed in 2 days (previously weeks)",
      "$12,000 / month saved from eliminated manual work",
      ">$5M collected in one click",
      "Automatic journal entries in NetSuite",
    ],
    viz: "line",
    color: "sky",
  },
  {
    id: "zenskar-indigov",
    vendor: "Zenskar",
    vendorUrl: "https://www.zenskar.com",
    sourceUrl: "https://www.zenskar.com/case-study/indigov",
    customer: "IndiGov",
    customerBio: "SaaS platform modernising constituent services for US government offices.",
    industry: "GovTech · SaaS",
    industryKey: "government",
    size: "US GovTech SaaS",
    metric: "-80%",
    metricLabel: "close time",
    summary: "40+ hours / month saved on finance ops.",
    problem: "Complex revenue recognition and billing slowed the monthly close.",
    solution:
      "Zenskar's rev-rec engine and QuickBooks integration handled contract-level allocations and journal automation.",
    improvements: "Initial contract mapping required review by the CFO's office.",
    timeline: "Not disclosed publicly",
    roi: [
      "80% faster close",
      "40+ hours / month saved on finance ops",
      "Automated rev-rec into QuickBooks",
    ],
    viz: "heat",
    color: "amber",
  },
  {
    id: "zenskar-thriva",
    vendor: "Zenskar",
    vendorUrl: "https://www.zenskar.com",
    sourceUrl: "https://www.zenskar.com/case-study/thriva",
    customer: "Thriva",
    customerBio: "UK-based preventative healthcare and blood-testing platform.",
    industry: "Healthcare · DTC Diagnostics",
    industryKey: "healthcare",
    size: "UK healthcare SaaS",
    metric: "D5 → D1",
    metricLabel: "invoices",
    summary: "Close moved from day 8 to day 3-4. 10-15% efficiency gain.",
    problem: "Complex healthcare billing delayed invoices and close cycles.",
    solution:
      "Zenskar consolidated billing on top of BigQuery, replacing Omni and hand-written SQL for revenue reporting.",
    improvements: "Existing Omni dashboards required rebuild inside Zenskar.",
    timeline: "Not disclosed publicly",
    roi: [
      "Invoices on day 1 (from day 5)",
      "Close from day 8 to day 3-4",
      "10-15% efficiency gain across finance ops",
    ],
    viz: "bars",
    color: "emerald",
  },
  {
    id: "zenskar-cyflare",
    vendor: "Zenskar",
    vendorUrl: "https://www.zenskar.com",
    sourceUrl: "https://www.zenskar.com/case-study/cyflare",
    customer: "Cyflare",
    customerBio: "US-based managed detection and response (MDR) provider serving channel partners.",
    industry: "Cybersecurity · SaaS",
    industryKey: "saas",
    size: "MSSP · channel-led",
    metric: "9d → 2d",
    metricLabel: "invoice cycle",
    summary: "Channel billing and custom pricing automated on PostgreSQL.",
    problem: "Channel billing and custom pricing caused a 9-day invoice cycle.",
    solution:
      "Zenskar automated pricing rules and journal creation, pulling directly from Cyflare's PostgreSQL usage source.",
    improvements: "Partner-tier pricing rules were iterated in the first two cycles.",
    timeline: "Not disclosed publicly",
    roi: [
      "Invoice cycle from 9 days to 2 days",
      "PostgreSQL as the usage source of truth",
      "Custom channel pricing rules automated",
    ],
    viz: "line",
    color: "sky",
  },

  // ─── FinaHQ (5) ───────────────────────────────────────────────────────
  {
    id: "finahq-neuberg",
    vendor: "FinaHQ",
    vendorUrl: "https://finahq.com",
    sourceUrl: "https://finahq.com/customer-cases/",
    customer: "Neuberg Diagnostics",
    customerBio:
      "Global diagnostic and pathology labs group across India, Middle East, USA and South Africa.",
    industry: "Healthcare · Diagnostics",
    industryKey: "healthcare",
    size: "75 entities",
    metric: "70k",
    metricLabel: "GL codes mapped",
    summary: "Instant consolidated + dimensional reporting across 75 entities.",
    problem:
      "75 entities across SAP, QuickBooks and Tally required manual consolidation. Audits were stressful and MIS vs actuals routinely mismatched.",
    solution:
      "FinaHQ's AI mapped 70,000 GL codes across ERPs, streamlined the Chart of Accounts and delivered instant separate, consolidated and dimensional reporting. FinaHQ states it managed 90% of onboarding.",
    improvements: "No quantified time saving published.",
    timeline: "Not disclosed publicly",
    roi: [
      "70,000 GL codes AI-mapped across SAP, QuickBooks and Tally",
      "Instant separate, consolidated and dimensional reporting",
      "FinaHQ managed 90% of onboarding",
    ],
    viz: "heat",
    color: "amber",
  },
  {
    id: "finahq-veranda",
    vendor: "FinaHQ",
    vendorUrl: "https://finahq.com",
    sourceUrl: "https://finahq.com/customer-cases/",
    customer: "Veranda Learning Solutions",
    customerBio: "Publicly listed Indian ed-tech and test-prep group.",
    industry: "EdTech",
    industryKey: "education",
    size: "25+ subsidiaries · listed",
    metric: "Day 0",
    metricLabel: "monthly reports",
    summary: "Reports from 8 days to Day 0. Delivered in 4 weeks.",
    problem: "25+ subsidiaries created a heavy monthly and SEBI reporting burden.",
    solution:
      "FinaHQ automated GL mapping across multiple Tally systems, intercompany reconciliation and SEBI templates.",
    improvements: "Vendor page also references a 10-day baseline elsewhere — treat with care.",
    timeline: "4 weeks",
    roi: [
      "Reports from 8 days to Day 0",
      ">1 week saved every month",
      "Intercompany reconciliation across 25+ entities",
    ],
    viz: "bars",
    color: "emerald",
  },
  {
    id: "finahq-swelect",
    vendor: "FinaHQ",
    vendorUrl: "https://finahq.com",
    sourceUrl: "https://finahq.com/wp-content/uploads/2025/08/Swelect.pdf",
    customer: "Swelect Energy Systems",
    customerBio: "Publicly listed Indian renewable-energy group across solar, storage and water.",
    industry: "Renewable Energy",
    industryKey: "energy",
    size: "20+ entities · listed",
    metric: "-95%",
    metricLabel: "PDF gen time",
    summary: "Same-day board documents. Zero material errors.",
    problem: "20+ entities on SAP and Tally required manual consolidation and slow signature-ready statements.",
    solution:
      "FinaHQ delivered custom PDF and Excel templates on top of consolidated data tables — remote implementation in 4 weeks.",
    improvements: "Template design for regulator-approved formats required review cycles.",
    timeline: "4 weeks",
    roi: [
      ">1 week saved per reporting cycle",
      "95% faster PDF generation",
      "100% same-day board documents",
      "Zero material errors",
    ],
    viz: "line",
    color: "sky",
  },
  {
    id: "finahq-snr",
    vendor: "FinaHQ",
    vendorUrl: "https://finahq.com",
    sourceUrl: "https://finahq.com/customer-cases/",
    customer: "Sri Ramakrishna Group",
    customerBio:
      "Coimbatore-based conglomerate running 25+ units across colleges, hospitals and pharmacies.",
    industry: "Education & Healthcare",
    industryKey: "education",
    size: "25+ units",
    metric: "15d → 2d",
    metricLabel: "book close",
    summary: "15,000+ Tally GL codes and monthly GST close, automated.",
    problem: "25+ units and 15,000+ Tally GL codes stretched the monthly close to 15 days.",
    solution:
      "FinaHQ built deep Tally connectors, automated monthly GSTR-based GST close and revenue-recognition across colleges.",
    improvements: "Cross-unit COA cleanup was a heavier one-time lift.",
    timeline: "Not disclosed publicly",
    roi: [
      "Book close from 15 days to 2 days",
      "GST close automated on GSTR reports",
      "Automated revenue recognition across colleges",
    ],
    viz: "heat",
    color: "amber",
  },
  {
    id: "finahq-samayanallur",
    vendor: "FinaHQ",
    vendorUrl: "https://finahq.com",
    sourceUrl: "https://finahq.com/customer-cases/",
    customer: "Samayanallur / Madurai Power",
    customerBio:
      "Diversified group across power, aircraft maintenance, hospitality and realty.",
    industry: "Diversified",
    industryKey: "other",
    size: "15+ legal entities",
    metric: "Real-time",
    metricLabel: "group reporting",
    summary: "18 months of Tally and custom-system history transitioned. Excel gone.",
    problem: "15+ entities on Tally and custom applications, Excel-based consolidation, no unified COA.",
    solution:
      "FinaHQ integrated Tally and custom systems, transitioned 18 months of prior data and delivered real-time separate, consolidated and dimensional reporting.",
    improvements: "No quantified ROI published.",
    timeline: "Not disclosed publicly",
    roi: [
      "Real-time separate, consolidated and dimensional reporting",
      "18 months of historical data transitioned",
      "Excel-based consolidation replaced",
    ],
    viz: "bars",
    color: "emerald",
  },

  // ─── ZenStatement (1) ─────────────────────────────────────────────────
  {
    id: "zenstatement-teabox",
    vendor: "ZenStatement",
    vendorUrl: "https://zenstatement.com",
    sourceUrl: "https://zenstatement.com/pricing/",
    customer: "Teabox",
    customerBio:
      "Indian premium tea D2C brand shipping to consumers worldwide.",
    industry: "Retail · D2C",
    industryKey: "retail",
    size: "D2C brand",
    metric: "Leaks",
    metricLabel: "uncovered",
    summary: "Revenue leakage found. Reconciliation across orders, payouts and logistics.",
    problem: "Needed unified visibility across orders, payouts and logistics for revenue integrity.",
    solution:
      "ZenStatement reconciled orders, payouts and logistics feeds, surfacing leakage and faster payment matching.",
    improvements: "Testimonial-level detail — no quantified metric published.",
    timeline: "Not disclosed publicly",
    roi: [
      "Revenue leakage surfaced",
      "Reconciliation across orders, payouts and logistics",
      "Faster payment matching",
    ],
    viz: "line",
    color: "sky",
  },

  // ─── Triam AI (3) ─────────────────────────────────────────────────────
  {
    id: "triam-barrix",
    vendor: "Triam AI",
    vendorUrl: "https://triam.ai",
    sourceUrl: "https://triam.ai",
    customer: "Barrix",
    customerBio: "SME using Triam AI for close and bookkeeping automation.",
    industry: "SME · Accounting",
    industryKey: "other",
    size: "SME",
    metric: "3d → 2h",
    metricLabel: "monthly close",
    summary: "Close time cut from 3 days to under 2 hours (~15x).",
    problem: "Monthly book close took 3 days across manual entries and reconciliations.",
    solution:
      "Triam AI's conversational accounting engine automated entries, reconciliations and reporting.",
    improvements: "Testimonial — method not published.",
    timeline: "Not disclosed publicly",
    roi: [
      "Close time from 3 days to under 2 hours",
      "~15x faster monthly close",
      "Conversational entry + reconciliation",
    ],
    viz: "heat",
    color: "amber",
  },
  {
    id: "triam-va-consulting",
    vendor: "Triam AI",
    vendorUrl: "https://triam.ai",
    sourceUrl: "https://triam.ai",
    customer: "V&A Consulting",
    customerBio: "Consulting firm using Triam AI for client financial visibility.",
    industry: "Professional Services",
    industryKey: "it-services",
    size: "Consulting firm",
    metric: "+40%",
    metricLabel: "client satisfaction",
    summary: "Faster financial visibility across client portfolios.",
    problem: "Needed faster financial visibility across client portfolios.",
    solution: "Triam AI provided conversational reporting and real-time MIS across engagements.",
    improvements: "Testimonial — measurement method not published.",
    timeline: "Not disclosed publicly",
    roi: [
      "40% more satisfied clients (claimed)",
      "Faster real-time reporting",
      "Conversational finance queries",
    ],
    viz: "bars",
    color: "emerald",
  },
  {
    id: "triam-tefty",
    vendor: "Triam AI",
    vendorUrl: "https://triam.ai",
    sourceUrl: "https://triam.ai",
    customer: "Tefty Organics",
    customerBio: "Organic consumer brand using Triam AI for autonomous bookkeeping.",
    industry: "CPG · Organics",
    industryKey: "cpg",
    size: "Consumer brand",
    metric: "99.8%",
    metricLabel: "accuracy claimed",
    summary: "Autonomous contextual accounting with high-accuracy entries.",
    problem: "Needed autonomous, contextual accounting instead of manual entries.",
    solution: "Triam AI's autonomous engine handled contextual entries with claimed 99.8% accuracy.",
    improvements: "Testimonial — measurement method not published.",
    timeline: "Not disclosed publicly",
    roi: [
      "99.8% accuracy claimed",
      "Autonomous contextual entries",
      "Manual bookkeeping eliminated",
    ],
    viz: "line",
    color: "sky",
  },
];

export default CASES;
export const getCaseById = (id) => CASES.find((c) => c.id === id);
export const casesByIndustryKey = (key) =>
  key ? CASES.filter((c) => c.industryKey === key) : CASES;
export const relatedCases = (id) => {
  const target = CASES.find((c) => c.id === id);
  if (!target) return [];
  return CASES.filter((c) => c.id !== id && c.industryKey === target.industryKey);
};
